import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import { ArrowLeft, Clock, Calendar, FileText, User, Users, Briefcase, DollarSign, MapPin, Mail, Layers, Target, Search } from 'lucide-react';
import { getCriteriaForType } from '@/utils/criteria';
import ProposalForm from '@/components/ProposalForm';
import axios from 'axios';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdDetailPage = () => {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '', name: '' });
  const [isSignup, setIsSignup] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchAdDetail();
  }, [adId]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  };

  const fetchAdDetail = async () => {
    try {
      const response = await axios.get(`${API}/acquirer-ads/${adId}`);
      setAd(response.data);
    } catch (error) {
      console.error('Error fetching ad:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePropose = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    } else if (currentUser.user_type === 'acquereur') {
      toast.info('Vous devez être vendeur pour proposer un bien');
    } else {
      setShowProposalForm(true);
    }
  };

  const handleLoginSubmit = async () => {
    try {
      if (isSignup) {
        await axios.post(`${API}/auth/signup`, {
          email: loginForm.email,
          name: loginForm.name,
          password: loginForm.password,
          user_type: 'vendeur'
        }, { withCredentials: true });
        toast.success('Compte créé !');
      } else {
        await axios.post(`${API}/auth/login`, {
          email: loginForm.email,
          password: loginForm.password
        }, { withCredentials: true });
        toast.success('Connexion réussie !');
      }
      setShowLoginModal(false);
      window.location.reload();
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 
                       error.message || 
                       'Erreur de connexion. V\u00e9rifiez votre connexion internet.';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Annonce non trouvée</h2>
          <Button onClick={() => navigate('/')} className="rounded-full">Retour</Button>
        </div>
      </div>
    );
  }

  const criteriaCategories = getCriteriaForType(ad.property_type);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white border-b border-border py-3 md:py-4 px-4 md:px-6 mb-6 md:mb-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-4 w-full sm:w-auto">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-xs md:text-sm">
              <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-base md:text-2xl font-bold text-foreground line-clamp-1">{ad.title}</h1>
          </div>
          {(!currentUser || currentUser.user_type === 'vendeur' || currentUser.user_type === 'both') && (
            <Button
              onClick={handlePropose}
              className="bg-black hover:bg-black/90 text-white font-semibold px-4 md:px-6 py-2 md:py-3 text-sm w-full sm:w-auto"
            >
              <Mail className="w-3 h-3 md:w-4 md:h-4 mr-2" />
              Proposer un bien
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-4 pb-8 md:pb-12">
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          <div className="md:col-span-2 space-y-4 md:space-y-6">
            {/* Type & Budget */}
            <div className="bg-gradient-to-br from-primary to-primary/80 rounded-lg p-4 md:p-8 text-white shadow-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-3">
                <div className="flex items-center gap-3 md:gap-4">
                  <div>
                    <h2 className="font-display text-xl md:text-3xl font-bold">
                      {ad.property_type} {ad.rooms > 0 && `${ad.rooms} pièces`}
                    </h2>
                    <p className="text-xs md:text-sm opacity-90 mt-1">{ad.usage_type}</p>
                  </div>
                </div>
                {ad.is_urgent && (
                  <span className="bg-red-600 text-white px-3 md:px-4 py-1 md:py-2 rounded font-bold text-xs md:text-sm">URGENT</span>
                )}
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded p-3 md:p-4">
                <p className="text-xs md:text-sm opacity-90 mb-2 flex items-center gap-2">
                  <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
                  Budget
                </p>
                <p className="text-xl md:text-3xl font-bold">
                  {ad.min_budget && `${ad.min_budget.toLocaleString('fr-FR')} € - `}
                  {ad.max_budget.toLocaleString('fr-FR')} €
                </p>
                {ad.financing_type && (
                  <p className="text-xs md:text-sm mt-2 bg-green-500 text-white px-2 md:px-3 py-1 rounded inline-block">
                    ✓ {ad.financing_type}
                  </p>
                )}
              </div>
            </div>

            {/* Description projet */}
            {ad.project_description && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-4 md:p-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4">Projet</h3>
                <p className="font-body text-sm md:text-base text-foreground leading-relaxed">{ad.project_description}</p>
              </div>
            )}

            {/* Localisation */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-4 md:p-6">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                Zone de recherche
              </h3>
              <div className="space-y-3">
                {ad.location_data?.address ? (
                  <>
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground mb-1">Adresse</p>
                      <p className="text-base md:text-lg font-bold text-foreground">{ad.location_data.address}</p>
                    </div>
                    {ad.location_data.radius && (
                      <div>
                        <p className="text-xs md:text-sm text-muted-foreground mb-1">Rayon de recherche</p>
                        <p className="text-sm md:text-base font-semibold text-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                          Dans un rayon de {ad.location_data.radius >= 1000 ? `${(ad.location_data.radius/1000).toFixed(1)} km` : `${ad.location_data.radius} m`}
                        </p>
                      </div>
                    )}
                    {ad.location_data.coords && (
                      <div className="h-48 md:h-64 rounded-lg overflow-hidden border border-gray-200 mt-4">
                        <MapContainer
                          center={ad.location_data.coords}
                          zoom={14}
                          style={{ height: '100%', width: '100%' }}
                          scrollWheelZoom={false}
                        >
                          <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap'
                          />
                          <Marker position={ad.location_data.coords} />
                          {ad.location_data.radius && (
                            <Circle
                              center={ad.location_data.coords}
                              radius={ad.location_data.radius}
                              pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.15 }}
                            />
                          )}
                        </MapContainer>
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Zone</p>
                    <p className="text-lg font-bold text-gray-800">{ad.location}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Caractéristiques */}
            <div className="bg-white rounded-lg shadow-sm border border-border p-4 md:p-6">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4 flex items-center gap-2">
                <Layers className="w-4 h-4 md:w-5 md:h-5" />
                Caractéristiques
              </h3>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {(ad.min_surface || ad.max_surface) && (
                  <div className="bg-muted/50 p-3 md:p-4 rounded">
                    <p className="text-xs md:text-sm text-muted-foreground mb-1">Surface</p>
                    <p className="text-sm md:text-base font-bold text-foreground">
                      {ad.min_surface && ad.max_surface ? `${ad.min_surface} - ${ad.max_surface} m²` :
                       ad.min_surface ? `Min ${ad.min_surface} m²` :
                       ad.max_surface ? `Max ${ad.max_surface} m²` : ''}
                    </p>
                  </div>
                )}
                {ad.rooms && ad.rooms > 0 && (
                  <div className="bg-muted/50 p-3 md:p-4 rounded">
                    <p className="text-xs md:text-sm text-muted-foreground mb-1 flex items-center gap-1">
                      <Layers className="w-3 h-3 md:w-4 md:h-4" />
                      Pièces
                    </p>
                    <p className="text-sm md:text-base font-bold text-foreground">{ad.rooms} pièces</p>
                  </div>
                )}
              </div>
            </div>

            {/* Critères */}
            {ad.criteria && Object.keys(ad.criteria).length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                <h3 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Critères recherchés
                </h3>
                <div className="space-y-4">
                  {Object.entries(ad.criteria).map(([categoryKey, values]) => {
                    if (!values || values.length === 0) return null;
                    const category = criteriaCategories[categoryKey];
                    if (!category) return null;
                    
                    return (
                      <div key={categoryKey} className="bg-blue-50 p-4 rounded-xl">
                        <p className="text-sm font-semibold text-gray-700 mb-2">{category.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {values.map((val, i) => {
                            const option = category.options.find(opt => opt.value === val);
                            return option ? (
                              <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-semibold text-gray-800">
                                ✓ {option.label}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-primary text-white rounded-lg p-4 md:p-6">
              <h3 className="font-heading text-base md:text-lg font-bold mb-2 flex items-center gap-2">
                <Search className="w-4 h-4 md:w-5 md:h-5" />
                RECHERCHE ACTIVE
              </h3>
              <p className="text-xs md:text-sm">Depuis {ad.active_since || '1 semaine'}</p>
            </div>

            {/* Planning */}
            {(ad.visit_possible || ad.decision_time || ad.signature_time) && (
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl">
                <h3 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 md:w-5 md:h-5" />
                  Planning
                </h3>
                <div className="space-y-2 md:space-y-3">
                  {ad.visit_possible && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <Clock className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-600">Visite possible</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.visit_possible}</p>
                      </div>
                    </div>
                  )}
                  {ad.decision_time && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <Calendar className="w-3 h-3 md:w-4 md:h-4 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-600">Décision sous</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.decision_time}</p>
                      </div>
                    </div>
                  )}
                  {ad.signature_time && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <FileText className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-600">Signature dans</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.signature_time}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Profil */}
            {(ad.family_composition || ad.annual_income || ad.personal_apport || ad.employment_status) && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-4 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground mb-3 md:mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 md:w-5 md:h-5" />
                  Profil
                </h3>
                <div className="space-y-2 md:space-y-3">
                  {ad.family_composition && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <Users className="w-3 h-3 md:w-4 md:h-4 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-600">Famille</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.family_composition}</p>
                      </div>
                    </div>
                  )}
                  {ad.annual_income && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <DollarSign className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-600">Revenus</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.annual_income.toLocaleString('fr-FR')} €/an</p>
                      </div>
                    </div>
                  )}
                  {ad.personal_apport && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <Briefcase className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-600">Apport</p>
                        <p className="text-sm md:text-base font-bold text-gray-800">{ad.personal_apport.toLocaleString('fr-FR')} €</p>
                      </div>
                    </div>
                  )}
                  {ad.employment_status && (
                    <div className="flex items-center gap-2 md:gap-3">
                      <Briefcase className="w-4 h-4 text-orange-600" />
                      <div>
                        <p className="text-xs text-gray-600">Emploi</p>
                        <p className="font-bold text-gray-800">{ad.employment_status}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CTA */}
            {(!currentUser || currentUser.user_type === 'vendeur' || currentUser.user_type === 'both') && (
              <div className="bg-accent/10 border-2 border-accent rounded-lg p-8 text-center">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                  Votre bien correspond ?
                </h3>
                <Button
                  onClick={handlePropose}
                  className="bg-black hover:bg-black/90 text-white rounded px-8 py-4 font-bold text-lg"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Proposer maintenant
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showProposalForm && (
        <ProposalForm
          ad={ad}
          onClose={() => setShowProposalForm(false)}
          onSuccess={() => {
            setShowProposalForm(false);
            toast.success('Proposition envoyée !');
          }}
        />
      )}

      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md !z-[9999]" style={{ zIndex: 9999 }}>
          <DialogHeader>
            <DialogTitle>{isSignup ? 'Créer un compte vendeur' : 'Connexion'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Tabs value={isSignup ? 'signup' : 'login'} onValueChange={(v) => setIsSignup(v === 'signup')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Connexion</TabsTrigger>
                <TabsTrigger value="signup">Inscription</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="space-y-3">
              {isSignup && (
                <div>
                  <Label htmlFor="name">Nom</Label>
                  <Input
                    id="name"
                    value={loginForm.name}
                    onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                    placeholder="Votre nom"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button onClick={handleLoginSubmit} className="w-full">
              {isSignup ? 'Créer mon compte' : 'Se connecter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdDetailPage;
